import {
  getAllPassengers,
  type PassengerInfo,
} from '@backoffice-frontend/ad-passenger-list'
import { useServiceCancellation } from '@backoffice-frontend/ad-vehicle-stream'
import { FccServiceCancellationPassengerAuthenticationStatusEnum } from '@backoffice-frontend/graphql/subscription'

import {
  Divider,
  LinearProgress,
  MoiaLabelCheckbox,
  useTheme,
} from '@backoffice-frontend/patterns'
import {
  CheckmarkIcon,
  ClockCounterIcon,
  XCircleIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import { Button, Icon, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PassengerCard } from '../../PassengerCard'
import { AuthenticationStatus } from '../../PassengerCard/PassengerCard'
import { TelematicsCheck } from '../../TelematicsCheck'
import { ActionCard } from '../ActionCard'
import type { AdVehicleStreamSingleTripCancellationActionCardVehicleFragment } from './singleTripCancellationActionCard.hook'

enum TripState {
  BOARDED = 'BOARDED',
  UPCOMING = 'UPCOMING',
  OTHER = 'OTHER',
}

export type PassengerInfoWithTripType = PassengerInfo & {
  tripState: TripState
  authenticationStatus: AuthenticationStatus
}

const FccServiceCancellationPassengerAuthenticationStatusEnumToAuthenticationStatus: Record<
  FccServiceCancellationPassengerAuthenticationStatusEnum,
  AuthenticationStatus
> = {
  [FccServiceCancellationPassengerAuthenticationStatusEnum.ManualPassengerCheckInPending]:
    AuthenticationStatus.MANUAL_PASSENGER_CHECK_IN_PENDING,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.ManualNoShowRequested]:
    AuthenticationStatus.MANUAL_NO_SHOW_REQUESTED,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.NoShowTriggered]:
    AuthenticationStatus.NO_SHOW_TRIGGERED,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.InProgress]:
    AuthenticationStatus.IN_PROGRESS,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.Confirmed]:
    AuthenticationStatus.CONFIRMED,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.Cancelled]:
    AuthenticationStatus.CANCELLED,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.ManualNoShowPending]:
    AuthenticationStatus.MANUAL_NO_SHOW_PENDING,
  [FccServiceCancellationPassengerAuthenticationStatusEnum.Upcoming]:
    AuthenticationStatus.UPCOMING,
}

export const SingleTripCancellationActionCard = ({
  vehicle,
}: {
  vehicle: AdVehicleStreamSingleTripCancellationActionCardVehicleFragment
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [selectedTrips, setSelectedTrips] = useState<string[]>([])
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isTelematicsOverwritten, setIsTelematicsOverwritten] = useState(false)

  const { abandon, cancelSomeTips, state, trips, isTripManagementAllowed } =
    useServiceCancellation()

  const vehicleIsStanding = vehicle.latestADTelemetry?.speed?.value === 0

  const allPassengers = useMemo(
    () =>
      getAllPassengers(vehicle.fccUpcomingWaypoints ?? []).reduce(
        (acc: PassengerInfoWithTripType[], passenger) => {
          const matchingTrip = trips?.find(
            trip => trip.tripId === passenger.trip.id,
          )
          if (matchingTrip) {
            const tripState = matchingTrip.isBoarded
              ? TripState.BOARDED
              : matchingTrip.passengerAuthenticationStatus ===
                  FccServiceCancellationPassengerAuthenticationStatusEnum.Upcoming
                ? TripState.UPCOMING
                : TripState.OTHER

            acc.push({
              ...passenger,
              authenticationStatus:
                FccServiceCancellationPassengerAuthenticationStatusEnumToAuthenticationStatus[
                  matchingTrip.passengerAuthenticationStatus
                ],
              tripState,
            })
          } else if (passenger.tripIsCancelled) {
            acc.push({
              ...passenger,
              authenticationStatus: AuthenticationStatus.CANCELLED,
              tripState: TripState.OTHER,
            })
          }
          return acc
        },
        [],
      ),
    [trips, vehicle.fccUpcomingWaypoints],
  )

  const selectablePassengers = useMemo(
    () =>
      allPassengers.filter(
        p =>
          p.tripState === TripState.BOARDED ||
          p.tripState === TripState.UPCOMING,
      ),
    [allPassengers],
  )

  useEffect(() => {
    if (selectablePassengers.length === 0) {
      setSelectedTrips([])
      setIsConfirmed(false)
      return
    }

    setSelectedTrips(prevSelectedTrips => {
      const selectableTripIds = new Set(
        selectablePassengers.map(p => p.trip.id),
      )
      const validSelectedTrips = prevSelectedTrips.filter(id =>
        selectableTripIds.has(id),
      )

      if (validSelectedTrips.length !== prevSelectedTrips.length) {
        if (validSelectedTrips.length === 0) {
          setIsConfirmed(false)
        }
        return validSelectedTrips
      }

      return prevSelectedTrips
    })
  }, [selectablePassengers])

  if (
    state.matches('idle') ||
    !isTripManagementAllowed ||
    allPassengers.length === 0
  ) {
    return (
      <ActionCard
        headline={
          isConfirmed
            ? t('adFleetMap_manageTripsConfirmTripActions_header')
            : t('adFleetMap_manageTripsSelectTrips_header')
        }
        instructions={''}
      >
        <LinearProgress />
      </ActionCard>
    )
  }

  const boardedPassengers = allPassengers.filter(
    p => p.tripState === TripState.BOARDED,
  )
  const upcomingPassengers = allPassengers.filter(
    p => p.tripState === TripState.UPCOMING,
  )
  const otherPassengers = allPassengers.filter(
    p => p.tripState === TripState.OTHER,
  )

  // Check if any selected trips are from boarded passengers
  const hasSelectedBoardedTrips = selectedTrips.some(tripId =>
    boardedPassengers.some(p => p.trip.id === tripId),
  )

  const toggleTripSelection = (tripId: string) => {
    setSelectedTrips(prevSelectedTrips =>
      prevSelectedTrips.includes(tripId)
        ? prevSelectedTrips.filter(id => id !== tripId)
        : [...prevSelectedTrips, tripId],
    )
  }

  const getActionText = (
    passenger: PassengerInfoWithTripType,
    tripId: string,
  ) => {
    if (passenger.tripState === TripState.BOARDED) {
      return (
        <Text
          size="md"
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space2,
          })}
        >
          <Icon size={20}>
            <ClockCounterIcon />
          </Icon>
          {t('map_earlyDeboarding_label')}
        </Text>
      )
    }

    if (passenger.tripState === TripState.UPCOMING) {
      return (
        <Text
          size="md"
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space2,
          })}
        >
          <Icon size={20}>
            <XCircleIcon />
          </Icon>
          {t('map_tripCancellation_label')}
        </Text>
      )
    }

    if (!isConfirmed) {
      return (
        <Text
          size="md"
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space2,
          })}
        >
          <Icon size={20}>
            <XIcon />
          </Icon>
          {passenger.authenticationStatus === AuthenticationStatus.CANCELLED
            ? t('adFleetMap_earlyDeboardingCardTripCancelledInfo_caption')
            : t('adFleetMap_manageTripsNonSelectableTripInfo_caption')}
        </Text>
      )
    }

    return selectedTrips.includes(tripId) ? (
      <Text
        size="md"
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
        })}
      >
        <Icon size={20}>
          <CheckmarkIcon color={theme.semantic.ColorAccentDefault} />
        </Icon>
        {t('adFleetMap_Selected_label')}
      </Text>
    ) : null
  }

  const getActionButton = (passenger: PassengerInfoWithTripType) => {
    if (isConfirmed) {
      return undefined
    }

    if (selectedTrips.includes(passenger.trip.id)) {
      return (
        <Button
          variant="secondary"
          label={t('adFleetMap_manageTripsRemoveSelection_button')}
          icon={<XIcon />}
          onClick={() => toggleTripSelection(passenger.trip.id)}
        />
      )
    }

    return (
      <Button
        variant={'secondary'}
        label={t('adFleetMap_manageTripsSelectTrip_button')}
        icon={
          selectedTrips.some(tripId => tripId === passenger.trip.id) ? (
            <XIcon />
          ) : undefined
        }
        disabled={passenger.tripState === TripState.OTHER}
        onClick={() => toggleTripSelection(passenger.trip.id)}
      />
    )
  }

  type SupportedTripState = TripState.BOARDED | TripState.UPCOMING

  const getTripSectionHeader = (tripState: SupportedTripState) => {
    switch (tripState) {
      case TripState.BOARDED:
        return {
          title: t('map_earlyDeboarding_label'),
          subtitle: t(
            'adFleetMap_manageTripsConfirmTripActionsEarlyDeboardingBundle_caption',
          ),
        }
      case TripState.UPCOMING:
        return {
          title: t('map_tripCancellation_label'),
          subtitle: t(
            'adFleetMap_manageTripsConfirmTripActionsTripCancellationBundle_caption',
          ),
        }
    }
  }

  const getTripSection = (
    passengers: PassengerInfoWithTripType[],
    tripState: TripState,
  ) => {
    if (passengers.length === 0) {
      return null
    }

    // Other trips can never be selected, so don't show them on confirm screen
    if (isConfirmed && tripState === TripState.OTHER) {
      return null
    }

    // Filter passengers based on confirmation state
    const visiblePassengers = isConfirmed
      ? passengers.filter(p => selectedTrips.includes(p.trip.id))
      : passengers

    // Don't render section if no visible passengers
    if (visiblePassengers.length === 0) {
      return null
    }

    const header =
      tripState !== TripState.OTHER ? getTripSectionHeader(tripState) : null

    return (
      <div
        key={tripState}
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space4,
        })}
      >
        {isConfirmed && header && (
          <div css={theme => ({ marginBottom: theme.spacing(-1) })}>
            <Text size="lg" weight="emphasized" css={{ display: 'block' }}>
              {header.title}
            </Text>
            <Text size="md" weight="regular" css={{ display: 'block' }}>
              {header.subtitle}
            </Text>
          </div>
        )}
        {visiblePassengers.map(passenger => (
          <PassengerCard
            actionText={getActionText(passenger, passenger.trip.id)}
            actionButton={getActionButton(passenger)}
            key={passenger.trip.id}
            customer={passenger.customer}
            waypointStop={passenger.stop}
            seats={passenger.seats}
            eta={passenger.eta}
            authenticationStatus={passenger.authenticationStatus}
            variant={
              selectedTrips.includes(passenger.trip.id) ? 'accent' : 'info'
            }
            isNoShow={!!passenger.notShownUpAt}
            isBoarded={passenger.tripState === TripState.BOARDED}
          />
        ))}
      </div>
    )
  }

  return (
    <ActionCard
      headline={t(
        isConfirmed
          ? 'adFleetMap_manageTripsConfirmTripActions_header'
          : 'adFleetMap_manageTripsSelectTrips_header',
      )}
      instructions={''}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <div
          css={theme => ({
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
            padding: theme.spacing(4, 3),
          })}
        >
          {getTripSection(boardedPassengers, TripState.BOARDED)}
          {getTripSection(upcomingPassengers, TripState.UPCOMING)}
          {getTripSection(otherPassengers, TripState.OTHER)}
          {isConfirmed && hasSelectedBoardedTrips && (
            <TelematicsCheck
              title={
                vehicleIsStanding
                  ? t('adFleetMap_vehicleIsStanding_label')
                  : t('adFleetMap_vehicleIsMoving_label')
              }
              variant={
                vehicleIsStanding
                  ? 'success'
                  : isTelematicsOverwritten
                    ? 'overwrite'
                    : 'pending'
              }
            />
          )}
        </div>
        <div
          css={theme => ({
            background: theme.semantic.ColorSurfaceDefault,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
          })}
        >
          <Divider />
          <div
            css={theme => ({
              padding: theme.space.Space4,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space.Space4,
            })}
          >
            <div
              css={theme => ({
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: theme.space.Space2,
                alignItems: 'center',
              })}
            >
              {isConfirmed && hasSelectedBoardedTrips && (
                <div css={{ flex: '1 0 auto', minWidth: 'fit-content' }}>
                  <MoiaLabelCheckbox
                    checked={isTelematicsOverwritten}
                    onChange={() => {
                      setIsTelematicsOverwritten(value => !value)
                    }}
                    label={t('map_skipTelematics_caption')}
                    disabled={vehicleIsStanding}
                  />
                </div>
              )}
              <div
                css={theme => ({
                  marginLeft: 'auto',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: theme.space.Space2,
                  alignItems: 'center',
                  flexShrink: 0,
                })}
              >
                <Button
                  variant="secondary"
                  onClick={abandon}
                  label={t('map_dismiss_button')}
                  disabled={state.matches('confirmed')}
                />
                {isConfirmed ? (
                  <Button
                    variant="critical"
                    onClick={() => cancelSomeTips(selectedTrips)}
                    label={
                      selectedTrips.length === 1
                        ? t('adFleetMap_manageTripsConfirmTripAction_button')
                        : t('adFleetMap_manageTripsConfirmTripActions_button')
                    }
                    disabled={
                      selectedTrips.length === 0 ||
                      (hasSelectedBoardedTrips &&
                        !vehicleIsStanding &&
                        !isTelematicsOverwritten) ||
                      state.matches('confirmed')
                    }
                    icon={
                      state.matches('confirmed') ? (
                        <LoadingIndicator />
                      ) : undefined
                    }
                  />
                ) : (
                  <Button
                    onClick={() => setIsConfirmed(true)}
                    label={t('adFleetMap_manageTripsConfirmSelection_button')}
                    disabled={selectedTrips.length === 0}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ActionCard>
  )
}
